type NetworkStatus = 'online' | 'offline' | 'syncing';
type NetworkListener = (status: NetworkStatus) => void;

export class NetworkService {
    private _status: NetworkStatus = 'online';
    private _listeners: NetworkListener[] = [];
    private _heartbeatInterval: any = null;
    private _serverUrl: string = '';
    private _probePromise: Promise<boolean> | null = null;
    private _lastProbeAt: number = 0;
    private _lastProbeOnline: boolean = false;

    constructor() {
        this._status = navigator.onLine ? 'online' : 'offline';

        window.addEventListener('online', () => {
            // A network interface alone does not prove that I-PRO is reachable.
            this.checkConnectivity(true);
        });

        window.addEventListener('offline', () => {
            // Android WebView can emit a transient offline event while the server
            // is still reachable. Confirm it with a network-only server probe.
            this.checkConnectivity(true);
        });
    }

    get isOnline(): boolean {
        return this._status === 'online' || this._status === 'syncing';
    }

    get isSyncing(): boolean {
        return this._status === 'syncing';
    }

    get status(): NetworkStatus {
        return this._status;
    }

    setServerUrl(url: string) {
        const changed = this._serverUrl !== url;
        this._serverUrl = url;
        if (!this._heartbeatInterval) this.startHeartbeat();
        if (changed || this._status === 'offline') this.checkConnectivity();
    }

    setStatus(status: NetworkStatus) {
        if (this._status !== status) {
            const prev = this._status;
            this._status = status;
            this._listeners.forEach(fn => {
                try { fn(status); } catch (e) { console.error('[NetworkService] Listener error:', e); }
            });
        }
    }

    onStatusChange(listener: NetworkListener): () => void {
        this._listeners.push(listener);
        return () => {
            this._listeners = this._listeners.filter(fn => fn !== listener);
        };
    }

    private probeUrl(): string {
        try {
            const server = new URL(this._serverUrl || window.location.origin, window.location.href);
            return `${server.origin}/auth/check?network_probe=${Date.now()}`;
        } catch {
            return `/auth/check?network_probe=${Date.now()}`;
        }
    }

    async checkConnectivity(force: boolean = false): Promise<boolean> {
        const now = Date.now();
        if (!force && now - this._lastProbeAt < 5000) return this._lastProbeOnline;
        if (this._probePromise) return this._probePromise;

        this._lastProbeAt = now;
        const url = this.probeUrl();
        this._probePromise = (async () => {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 4000);
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    credentials: 'include',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    signal: controller.signal
                });
                if (!response.ok) {
                    throw new Error(`[NetworkService] Probe HTTP ${response.status}`);
                }
                const payload = await response.json();
                const validPayload = payload
                    && Number(payload.code) === 200
                    && payload.data
                    && typeof payload.data.status === 'boolean';
                if (!validPayload) {
                    throw new Error('[NetworkService] Invalid probe response');
                }
                this._lastProbeOnline = true;
                this.setStatus('online');
                return true;
            } catch {
                this._lastProbeOnline = false;
                this.setStatus('offline');
                return false;
            } finally {
                clearTimeout(timeout);
                this._probePromise = null;
            }
        })();
        return this._probePromise;
    }

    startHeartbeat(intervalMs: number = 15000) {
        this.stopHeartbeat();
        this._heartbeatInterval = setInterval(() => {
            this.checkConnectivity(true);
        }, intervalMs);
    }

    stopHeartbeat() {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }
}

let _networkInstance: NetworkService | null = null;

export function getNetworkService(): NetworkService {
    if (!_networkInstance) {
        _networkInstance = new NetworkService();
    }
    return _networkInstance;
}
